import apiClient from './apiClient';

// ============================================================
// Types — matching backend Go models for J203 (樣品交運單)
// ============================================================

export interface FJ130 {
  fj130_01: string;          // 交運單號 (PK)
  fj130_10: string;          // 樣品申請單號(來源)
  fj130_11: string | null;   // 交運日期 (ISO string)
  fj130_12: string;          // 客戶代號
  fj130_13: string | null;   // 需要日期
  fj130_14: string;          // 幣別
  fj130_15: number;          // 匯率
  fj130_16: boolean;         // 收費旗標
  fj130_17: boolean;         // 含稅旗標
  fj130_171: number;         // 發票種類 (1~5)
  fj130_18: number;          // 原幣未稅總額
  fj130_181: number;         // 原幣含稅總額
  fj130_19: number;          // 稅額
  fj130_20: number;          // 型別
  fj130_21: string;          // 業務代號
  fj130_22: string;          // 部門代號
  fj130_23: boolean;         // 扣庫存旗標
  fj130_24: string;          // 付款條件
  fj130_25: string | null;   // 付款到期日
  fj130_26: number;          // 明細筆數
  fj130_27: string;          // 備註代號
  fj130_31: string;          // 備註說明 (memo)
  fj130_52: string;          // 出貨通知單號
  fj130_80: string;          // 核准狀態 N=草稿/已沖銷 D=送審中 P=已核准（自創碼 S/R/V 已廢除）
  fj130_81: string;          // 沖銷狀態
  fj130_90: number;          // 版本號
  fj130_91: string;          // 異動人員
  fj130_92: string | null;   // 異動日期

  // Associations
  details?: FJ131[];
  board_details?: FJ132[];
  extras?: FJ133[];
  payments?: FG031[];
}

export interface FJ131 {
  fj131_01: string;    // FK to FJ130_01
  fj131_02: number;    // 項次
  fj131_10: string;    // 庫位代號
  fj131_11: string;    // 產品代號1
  fj131_12: string;    // 產品代號2
  fj131_13: string;    // 批號
  fj131_15: string;    // 單位
  fj131_16: number;    // 未稅單價
  fj131_161: number;   // 含稅單價
  fj131_17: number;    // 交運數量
  fj131_18: number;    // 換算率
  fj131_19: string;    // 色號
  fj131_191: string;   // 中文色名
  fj131_192: string;   // 英文色名
  fj131_20: string;    // 等級
  fj131_23: number;    // 未稅金額
  fj131_231: number;   // 含稅金額
  fj131_25: string;    // 備註
  fj131_90: number;

  // Sub-detail
  boards?: FJ132[];
}

export interface FJ132 {
  fj132_01: string;    // FK to FJ130_01
  fj132_02: number;    // FK to FJ131_02
  fj132_03: number;    // 板號序號
  fj132_11: string;    // 板號
  fj132_12: string;    // 板號備註
  fj132_90: number;
}

export interface FJ133 {
  fj133_01: string;    // FK to FJ130_01
  fj133_02: string;    // 序號/代碼
  fj133_11: string | null; // 日期
  fj133_12: number;    // 金額
}

export interface FG031 {
  fg031_01: string;          // FK to FJ130_01 (交運單號)
  fg031_02: string;          // 序號
  fg031_03: string;          // 繳款單號
  fg031_11: number;          // 繳款金額
  fg031_12: string | null;   // 繳款日期
  fg031_91: string;          // 異動人員
  fg031_92: string | null;   // 異動日期
  fg031_93: string;          // 建立人員
}

// ============================================================
// Request / Response types
// ============================================================

export interface J203ListResponse {
  data: FJ130[];
  total: number;
  page: number;
  size: number;
}

export interface J203CreateRequest {
  master: Partial<FJ130>;
  details: Partial<FJ131>[];
  board_details?: Partial<FJ132>[];
  extras?: Partial<FJ133>[];
  payments?: Partial<FG031>[];
}

export interface ValidationError {
  field: string;
  rule: string;
  message: string;
}

// ============================================================
// Approval types
// ============================================================

export interface ApprovalResponse {
  request_no: string;
  action: string;
  old_status: string;
  new_status: string;
  message: string;
}

export interface ApprovalHistory {
  id: number;
  request_no: string;
  action: string;
  actor_id: string;
  actor_name: string;
  comment: string;
  old_status: string;
  new_status: string;
  created_at: string;
}

// ============================================================
// API methods
// ============================================================

export const j203Api = {
  async list(page = 1, size = 20): Promise<J203ListResponse> {
    const res = await apiClient.get('/api/j203', { params: { page, size } });
    return res.data;
  },

  async get(id: string): Promise<FJ130> {
    const res = await apiClient.get(`/api/j203/${encodeURIComponent(id)}`);
    return res.data;
  },

  async create(data: J203CreateRequest): Promise<FJ130> {
    const res = await apiClient.post('/api/j203', data);
    return res.data;
  },

  async update(id: string, data: J203CreateRequest): Promise<FJ130> {
    const res = await apiClient.put(`/api/j203/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/j203/${encodeURIComponent(id)}`);
  },

  // ============================================================
  // Approval workflow
  // ============================================================

  async submitForApproval(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/j203/${encodeURIComponent(id)}/submit`, { comment: comment || '' });
    return res.data;
  },

  async approve(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/j203/${encodeURIComponent(id)}/approve`, { comment: comment || '' });
    return res.data;
  },

  async reject(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/j203/${encodeURIComponent(id)}/reject`, { comment: comment || '' });
    return res.data;
  },

  async writeoff(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/j203/${encodeURIComponent(id)}/writeoff`, { comment: comment || '' });
    return res.data;
  },

  async getApprovalHistory(id: string): Promise<ApprovalHistory[]> {
    const res = await apiClient.get(`/api/j203/${encodeURIComponent(id)}/approval-history`);
    return res.data.data || [];
  },

  // Lookup: reuse SC module's lookup endpoint
  async lookupName(type: string, code: string): Promise<string> {
    if (!code || !code.trim()) return '';
    try {
      const res = await apiClient.get(`/api/sc/lookup/${encodeURIComponent(type)}/${encodeURIComponent(code.trim())}`);
      return res.data.name || '';
    } catch {
      return '';
    }
  },
};
